import React, { useState, useEffect, useContext } from "react";
import {
    makeStyles,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Typography,
    Box,
    Chip,
    useTheme,
    useMediaQuery
} from "@material-ui/core";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon
} from "@material-ui/icons";

import MainContainer from "../MainContainer";
import MainHeader from "../MainHeader";
import MainHeaderButtonsWrapper from "../MainHeaderButtonsWrapper";
import Title from "../Title";
import TableRowSkeleton from "../TableRowSkeleton";
import PlanModal from "../PlanModal";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { usePlansContext } from "../../context/Plans/PlansContext";
import toastUtils from "../../utils/toastUtils";

// Helper function to ensure i18n returns a string
const t = (key, fallback = '') => {
    const result = i18n.t(key);
    return typeof result === 'string' ? result : (fallback || key.split('.').pop());
};

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(2),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    tableContainer: {
        marginTop: theme.spacing(2),
    },
    actionButtons: {
        display: 'flex',
        gap: theme.spacing(1),
    },
    chip: {
        margin: theme.spacing(0.5),
    },
    enabledChip: {
        backgroundColor: theme.palette.success.main,
        color: 'white',
    },
    disabledChip: {
        backgroundColor: theme.palette.grey[400],
        color: 'white',
    },
    addButton: {
        borderRadius: theme.spacing(3),
    }
}));

const PlansManager = () => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { user } = useContext(AuthContext);

    const { plans, loading, error, fetchPlans, savePlan, updatePlan, removePlan } = usePlansContext();

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedPlan, setSelectedPlan] = useState(null);

    useEffect(() => {
        // Data should already be loaded by the context
        // But we can refresh if needed
        if (plans.length === 0 && !loading) {
            fetchPlans();
        }
    }, [plans.length, loading, fetchPlans]);

    const handleCreatePlan = () => {
        setModalMode('create');
        setSelectedPlan(null);
        setModalOpen(true);
    };

    const handleEditPlan = (plan) => {
        setModalMode('edit');
        setSelectedPlan(plan);
        setModalOpen(true);
    };

    const handleSavePlan = async (planData) => {
        try {
            if (modalMode === 'create') {
                await savePlan(planData);
            } else {
                await updatePlan(planData);
            }
            
            await fetchPlans();
            setModalOpen(false);
            setSelectedPlan(null);
        } catch (error) {
            console.error("Error saving plan:", error);
            throw error; // Re-throw to let modal handle it
        }
    };

    const handleDeletePlan = async (plan) => {
        try {
            await removePlan(plan.id);
            await fetchPlans();
            setModalOpen(false);
            setSelectedPlan(null);
        } catch (error) {
            console.error("Error deleting plan:", error);
            throw error; // Re-throw to let modal handle it
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedPlan(null);
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    const getFeatureChip = (enabled, label) => {
        return (
            <Chip
                size="small"
                label={label}
                className={`${classes.chip} ${enabled ? classes.enabledChip : classes.disabledChip}`}
            />
        );
    };

    return (
        <MainContainer>
            <MainHeader>
                <Title>{t("plans.title", "Gerenciamento de Planos")}</Title>
                <MainHeaderButtonsWrapper>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCreatePlan}
                        startIcon={<AddIcon />}
                        className={classes.addButton}
                    >
                        {t("plans.buttons.add", "Adicionar Plano")}
                    </Button>
                </MainHeaderButtonsWrapper>
            </MainHeader>

            <Paper className={classes.mainPaper} variant="outlined">
                <TableContainer className={classes.tableContainer}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>{t("plans.table.name", "Nome")}</TableCell>
                                <TableCell align="center">{t("plans.table.users", "Usuários")}</TableCell>
                                <TableCell align="center">{t("plans.table.connections", "Conexões")}</TableCell>
                                <TableCell align="center">{t("plans.table.queues", "Filas")}</TableCell>
                                <TableCell align="center">{t("plans.table.amount", "Valor")}</TableCell>
                                <TableCell align="center">{t("plans.table.features", "Recursos")}</TableCell>
                                <TableCell align="center">{t("plans.table.actions", "Ações")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRowSkeleton avatar={false} columns={7} />
                            ) : (
                                plans.map((plan) => (
                                    <TableRow key={plan.id}>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {plan.name}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {plan.isPublic ? "Público" : "Privado"} • 
                                                    {plan.trial ? ` Trial ${plan.trialDays} dias` : " Sem trial"}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">{plan.users}</TableCell>
                                        <TableCell align="center">{plan.connections}</TableCell>
                                        <TableCell align="center">{plan.queues}</TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2" fontWeight="bold">
                                                {formatCurrency(plan.amount)}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {plan.recurrence === 'monthly' ? 'Mensal' : 'Anual'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box display="flex" flexWrap="wrap" justifyContent="center">
                                                {getFeatureChip(plan.useWhatsapp, "WhatsApp")}
                                                {getFeatureChip(plan.useCampaigns, "Campanhas")}
                                                {getFeatureChip(plan.useOpenAi, "IA")}
                                                {getFeatureChip(plan.useKanban, "Kanban")}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <div className={classes.actionButtons}>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditPlan(plan)}
                                                    title="Editar"
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {!loading && plans.length === 0 && (
                    <Box 
                        display="flex" 
                        justifyContent="center" 
                        alignItems="center" 
                        minHeight="200px"
                        flexDirection="column"
                    >
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                            {t("plans.cards.noPlans", "Nenhum plano encontrado")}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {t("plans.cards.createFirst", "Crie seu primeiro plano para começar")}
                        </Typography>
                    </Box>
                )}
            </Paper>

            <PlanModal
                open={modalOpen}
                mode={modalMode}
                initialValues={selectedPlan}
                onClose={handleCloseModal}
                onSave={handleSavePlan}
                onDelete={handleDeletePlan}
                loading={loading}
            />
        </MainContainer>
    );
};

export default PlansManager;